import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMailAdminAuth } from '../../hooks/useMailAdminAuth'
import {
  getDomain, listAccounts, createAccount, deleteAccount,
  getDnsRecords, createDkim, type Domain, type Account, type DnsRecord,
} from '../../lib/jmap-admin'

export function DomainDetail() {
  const { id } = useParams<{ id: string }>()
  const { auth } = useMailAdminAuth()
  const navigate = useNavigate()
  const [domain, setDomain] = useState<Domain | null>(null)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [dnsRecords, setDnsRecords] = useState<DnsRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateAccount, setShowCreateAccount] = useState(false)
  const [newAccount, setNewAccount] = useState({ name: '', password: '', description: '' })
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!auth || !id) { navigate('/admin/login'); return }
    loadData()
  }, [auth, id])

  const loadData = async () => {
    if (!auth || !id) return
    setLoading(true)
    try {
      const [d, accts] = await Promise.all([
        getDomain(auth, id),
        listAccounts(auth, id),
      ])
      setDomain(d)
      setAccounts(accts)
      if (d) setDnsRecords(await getDnsRecords(auth, d.name))
    } catch (e) {
      setError(`Failed to load: ${e}`)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!auth || !id || !newAccount.name.trim()) return
    setCreating(true)
    setError('')
    try {
      await createAccount(auth, {
        name: newAccount.name.trim(),
        domainId: id,
        password: newAccount.password || undefined,
        description: newAccount.description,
      })
      setNewAccount({ name: '', password: '', description: '' })
      setShowCreateAccount(false)
      await loadData()
    } catch (e) {
      setError(`Failed to create account: ${e}`)
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteAccount = async (accountId: string, name: string) => {
    if (!auth || !confirm(`Delete account "${name}"?`)) return
    try {
      await deleteAccount(auth, accountId)
      await loadData()
    } catch (e) {
      setError(`Failed to delete: ${e}`)
    }
  }

  if (loading) {
    return <div className="p-8 text-center text-text-muted">Loading...</div>
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button onClick={() => navigate('/admin/domains')} className="text-sm text-text-muted hover:text-text mb-4 inline-block">
          &larr; All domains
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold text-text">{domain?.name || 'Domain'}</h1>
            <p className="text-text-muted mt-1">{accounts.length} account{accounts.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={loadData} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-text-muted hover:bg-slate-50">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline">dismiss</button>
        </div>
      )}

      {/* DNS Records */}
      <section className="mb-8">
        <h2 className="font-ui text-lg font-semibold text-text mb-4">DNS Records</h2>
        <p className="text-sm text-text-muted mb-4">Add these records to your DNS provider to verify the domain and enable email delivery.</p>
        {dnsRecords.length > 0 ? (
          <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-text-muted">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-text-muted">Host</th>
                  <th className="px-4 py-3 text-left font-medium text-text-muted">Value</th>
                  <th className="px-4 py-3 text-left font-medium text-text-muted">Priority</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {dnsRecords.map((r, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 font-mono text-xs font-medium text-sovereign-600">{r.type}</td>
                    <td className="px-4 py-3 font-mono text-xs text-text">{r.host}</td>
                    <td className="px-4 py-3 font-mono text-xs text-text-muted break-all">{r.value}</td>
                    <td className="px-4 py-3 text-xs text-text-muted">{r.priority || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-slate-50 rounded-xl border border-dashed border-slate-300 p-6 text-center">
            <p className="text-sm text-text-muted">No DNS records generated yet. Generate DKIM keys first.</p>
            <button
              onClick={async () => {
                if (!auth || !domain) return
                try {
                  await createDkim(auth, domain.name)
                  await loadData()
                } catch (e) {
                  setError(`Failed to generate DKIM: ${e}`)
                }
              }}
              className="mt-3 px-4 py-2 bg-sovereign-600 text-white rounded-lg text-sm font-medium hover:bg-sovereign-700"
            >
              Generate DKIM Key
            </button>
          </div>
        )}
      </section>

      {/* Accounts */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-ui text-lg font-semibold text-text">Accounts</h2>
          <button
            onClick={() => setShowCreateAccount(true)}
            className="px-4 py-2 bg-sovereign-600 text-white rounded-lg text-sm font-medium hover:bg-sovereign-700"
          >
            Add Account
          </button>
        </div>

        {showCreateAccount && (
          <form onSubmit={handleCreateAccount} className="mb-4 bg-surface rounded-xl border border-sovereign-200 p-6 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Username</label>
                <div className="flex">
                  <input
                    type="text"
                    value={newAccount.name}
                    onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                    className="flex-1 px-3 py-2.5 border border-r-0 border-slate-200 rounded-l-lg text-sm focus:outline-none focus:border-sovereign-500"
                    placeholder="user"
                    autoFocus
                  />
                  <span className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-r-lg text-sm text-text-muted">
                    @{domain?.name}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Password</label>
                <input
                  type="password"
                  value={newAccount.password}
                  onChange={(e) => setNewAccount({ ...newAccount, password: e.target.value })}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-sovereign-500"
                  placeholder="Optional — set later"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Description</label>
              <input
                type="text"
                value={newAccount.description}
                onChange={(e) => setNewAccount({ ...newAccount, description: e.target.value })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-sovereign-500"
                placeholder="Optional"
              />
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={creating || !newAccount.name.trim()} className="px-6 py-2.5 bg-sovereign-600 text-white rounded-lg text-sm font-medium hover:bg-sovereign-700 disabled:opacity-50">
                {creating ? 'Creating...' : 'Create Account'}
              </button>
              <button type="button" onClick={() => { setShowCreateAccount(false); setNewAccount({ name: '', password: '', description: '' }) }} className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-text-muted hover:bg-slate-50">
                Cancel
              </button>
            </div>
          </form>
        )}

        {accounts.length === 0 ? (
          <div className="text-center py-12 bg-surface rounded-xl border border-slate-200">
            <p className="text-text-muted mb-4">No accounts in this domain</p>
            <button onClick={() => setShowCreateAccount(true)} className="px-6 py-2.5 bg-sovereign-600 text-white rounded-lg text-sm font-medium hover:bg-sovereign-700">
              Create first account
            </button>
          </div>
        ) : (
          <div className="bg-surface rounded-xl border border-slate-200 divide-y divide-slate-200">
            {accounts.map((acct) => (
              <div key={acct.id} className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-sovereign-50 flex items-center justify-center">
                    <span className="text-sovereign-600 font-semibold text-sm">
                      {acct.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <span className="font-ui font-medium text-text">{acct.name}@{domain?.name}</span>
                    {acct.description && <p className="text-xs text-text-muted">{acct.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    acct.enabled ? 'bg-trust/10 text-trust' : 'bg-slate-100 text-text-muted'
                  }`}>
                    {acct.enabled ? 'Active' : 'Disabled'}
                  </span>
                  <button onClick={() => handleDeleteAccount(acct.id, acct.name)} className="text-sm text-red-500 hover:text-red-700">
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
